import React, { useState } from 'react';
import { Match, MatchTeamAssignment, FootballPosition, GroupMemberProfile, PitchPlayer, RSVPStatus } from '../types';
import { Shield, Sparkles, Check, Users, ArrowRightLeft, UserCheck, ChevronRight } from 'lucide-react';

interface TeamPositionSelectorProps {
  match: Match;
  activeMember: GroupMemberProfile;
  membersMap: Map<string, GroupMemberProfile>;
  onUpdateTeams: (matchId: string, teamA: MatchTeamAssignment, teamB: MatchTeamAssignment) => void;
  onUpdateRSVP?: (status: RSVPStatus) => void;
}

const POSITION_COORDINATES: Record<FootballPosition, { x: number; y: number }> = {
  GK: { x: 50, y: 88 },
  CB: { x: 50, y: 72 },
  LB: { x: 22, y: 70 },
  RB: { x: 78, y: 70 },
  LWB: { x: 20, y: 60 },
  RWB: { x: 80, y: 60 },
  CDM: { x: 50, y: 58 },
  CM: { x: 50, y: 48 },
  CAM: { x: 50, y: 35 },
  LM: { x: 22, y: 45 },
  RM: { x: 78, y: 45 },
  LW: { x: 22, y: 25 },
  RW: { x: 78, y: 25 },
  ST: { x: 50, y: 18 },
  CF: { x: 50, y: 22 },
};

const POSITION_GROUPS = [
  { label: 'Attack', positions: ['ST', 'CF', 'LW', 'RW'] as FootballPosition[] },
  { label: 'Midfield', positions: ['CAM', 'CM', 'CDM', 'LM', 'RM'] as FootballPosition[] },
  { label: 'Defense & GK', positions: ['CB', 'LB', 'RB', 'LWB', 'RWB', 'GK'] as FootballPosition[] },
];

export const TeamPositionSelector: React.FC<TeamPositionSelectorProps> = ({
  match,
  activeMember,
  membersMap,
  onUpdateTeams,
  onUpdateRSVP
}) => {
  // Initialize default team structures if undefined
  const teamA: MatchTeamAssignment = match.teamA || {
    name: 'Team Green',
    color: '#10B981',
    formation: '2-3-1',
    players: []
  };

  const teamB: MatchTeamAssignment = match.teamB || {
    name: 'Team Blue',
    color: '#3B82F6',
    formation: '2-3-1',
    players: []
  };

  // Find active member's current placement
  const memberInA = teamA.players.find(p => p.memberProfileId === activeMember.id);
  const memberInB = teamB.players.find(p => p.memberProfileId === activeMember.id);
  const currentTeamKey: 'teamA' | 'teamB' | null = memberInA ? 'teamA' : memberInB ? 'teamB' : null;
  const currentPosition = memberInA?.position || memberInB?.position || activeMember.primaryPosition;

  const [selectedTeamKey, setSelectedTeamKey] = useState<'teamA' | 'teamB'>(currentTeamKey || 'teamA');
  const [selectedPosition, setSelectedPosition] = useState<FootballPosition>(currentPosition);
  const [justSaved, setJustSaved] = useState(false);

  const handleJoinOrMove = (targetTeamKey: 'teamA' | 'teamB', targetPos: FootballPosition) => {
    setSelectedTeamKey(targetTeamKey);
    setSelectedPosition(targetPos);

    const coords = POSITION_COORDINATES[targetPos] || { x: 50, y: 50 };

    // Remove active member from both teams
    const newPlayersA = teamA.players.filter(p => p.memberProfileId !== activeMember.id);
    const newPlayersB = teamB.players.filter(p => p.memberProfileId !== activeMember.id);

    const newPlayerEntry: PitchPlayer = {
      memberProfileId: activeMember.id,
      position: targetPos,
      pitchX: coords.x,
      pitchY: coords.y,
    };

    if (targetTeamKey === 'teamA') {
      newPlayersA.push(newPlayerEntry);
    } else {
      newPlayersB.push(newPlayerEntry);
    }

    const updatedTeamA = { ...teamA, players: newPlayersA };
    const updatedTeamB = { ...teamB, players: newPlayersB };

    onUpdateTeams(match.id, updatedTeamA, updatedTeamB);

    if (onUpdateRSVP) {
      onUpdateRSVP('going');
    }

    setJustSaved(true);
    setTimeout(() => setJustSaved(false), 2500);
  };

  const handleLeaveTeam = () => {
    const newPlayersA = teamA.players.filter(p => p.memberProfileId !== activeMember.id);
    const newPlayersB = teamB.players.filter(p => p.memberProfileId !== activeMember.id);
    onUpdateTeams(match.id, { ...teamA, players: newPlayersA }, { ...teamB, players: newPlayersB });
  };

  const isMatchCompleted = match.status === 'completed';

  return (
    <div className="bg-slate-900 border-2 border-emerald-500/40 rounded-3xl p-4 sm:p-5 shadow-2xl space-y-4">
      {/* Title & Current Status */}
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-800 pb-3">
        <div>
          <div className="flex items-center gap-1.5 text-xs font-black uppercase text-emerald-400">
            <Sparkles className="w-4 h-4" /> Team & Pitch Lineup
          </div>
          <h3 className="text-base font-extrabold text-white mt-0.5">
            {isMatchCompleted ? 'Match Lineup & Positions' : 'Where do you want to play?'}
          </h3>
        </div>

        {currentTeamKey ? (
          <div className="flex items-center gap-2">
            <span className="text-xs font-mono font-black uppercase px-3 py-1 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: currentTeamKey === 'teamA' ? teamA.color : teamB.color }} />
              Playing {currentPosition} ({currentTeamKey === 'teamA' ? teamA.name : teamB.name})
            </span>
            {!isMatchCompleted && (
              <button
                type="button"
                onClick={handleLeaveTeam}
                className="text-[10px] text-slate-400 hover:text-rose-400 uppercase font-bold underline cursor-pointer"
              >
                Leave Team
              </button>
            )}
          </div>
        ) : (
          <span className="text-xs font-mono font-bold text-amber-400 bg-amber-400/10 px-2.5 py-1 rounded-full border border-amber-400/30">
            {isMatchCompleted ? 'Unassigned' : 'Not assigned yet'}
          </span>
        )}
      </div>

      {/* Completed Lock Notice */}
      {isMatchCompleted && (
        <div className="bg-amber-500/10 border border-amber-500/30 p-3 rounded-2xl text-xs font-semibold text-amber-300 flex items-center gap-2">
          <span>🔒</span>
          <span>This match is completed. Squad lineups and pitch positions are locked for historical career stats.</span>
        </div>
      )}

      {/* Step 1: Select Team */}
      <div className="space-y-2">
        <label className="text-xs font-extrabold uppercase text-slate-300 block tracking-wider">
          {isMatchCompleted ? 'Teams:' : '1. Choose Your Team:'}
        </label>
        <div className="grid grid-cols-2 gap-3">
          {/* Team A Button */}
          <button
            type="button"
            disabled={isMatchCompleted}
            onClick={() => handleJoinOrMove('teamA', selectedPosition)}
            className={`p-3.5 rounded-2xl border-2 text-left transition-all relative overflow-hidden flex flex-col justify-between ${
              isMatchCompleted
                ? 'opacity-80 cursor-default bg-slate-950 border-slate-800'
                : selectedTeamKey === 'teamA' && currentTeamKey === 'teamA'
                ? 'bg-slate-950 border-emerald-400 ring-2 ring-emerald-500/30 shadow-lg shadow-emerald-500/20 active:scale-95'
                : 'bg-slate-950/80 hover:bg-slate-950 border-slate-800 active:scale-95'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-3.5 h-3.5 rounded-full shadow" style={{ backgroundColor: teamA.color }} />
                <span className="font-extrabold text-sm text-white">{teamA.name}</span>
              </div>
              {currentTeamKey === 'teamA' && (
                <span className="bg-emerald-500 text-slate-950 text-[10px] font-black px-2 py-0.5 rounded-full flex items-center gap-1">
                  <Check className="w-3 h-3 stroke-[3]" /> Selected
                </span>
              )}
            </div>
            <div className="mt-2 text-xs font-mono text-slate-400 flex items-center justify-between">
              <span>{teamA.players.length} Players</span>
              {!isMatchCompleted && <span className="text-emerald-400 font-bold">Tap to Join</span>}
            </div>
          </button>

          {/* Team B Button */}
          <button
            type="button"
            disabled={isMatchCompleted}
            onClick={() => handleJoinOrMove('teamB', selectedPosition)}
            className={`p-3.5 rounded-2xl border-2 text-left transition-all relative overflow-hidden flex flex-col justify-between ${
              isMatchCompleted
                ? 'opacity-80 cursor-default bg-slate-950 border-slate-800'
                : selectedTeamKey === 'teamB' && currentTeamKey === 'teamB'
                ? 'bg-slate-950 border-blue-400 ring-2 ring-blue-500/30 shadow-lg shadow-blue-500/20 active:scale-95'
                : 'bg-slate-950/80 hover:bg-slate-950 border-slate-800 active:scale-95'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-3.5 h-3.5 rounded-full shadow" style={{ backgroundColor: teamB.color }} />
                <span className="font-extrabold text-sm text-white">{teamB.name}</span>
              </div>
              {currentTeamKey === 'teamB' && (
                <span className="bg-blue-500 text-white text-[10px] font-black px-2 py-0.5 rounded-full flex items-center gap-1">
                  <Check className="w-3 h-3 stroke-[3]" /> Selected
                </span>
              )}
            </div>
            <div className="mt-2 text-xs font-mono text-slate-400 flex items-center justify-between">
              <span>{teamB.players.length} Players</span>
              {!isMatchCompleted && <span className="text-blue-400 font-bold">Tap to Join</span>}
            </div>
          </button>
        </div>
      </div>

      {/* Step 2: Select Pitch Position */}
      <div className="space-y-2">
        <label className="text-xs font-extrabold uppercase text-slate-300 block tracking-wider">
          {isMatchCompleted ? 'Positions Assigned:' : '2. Select Position on Pitch:'}
        </label>
        <div className="bg-slate-950 p-3 rounded-2xl border border-slate-800 space-y-3">
          {POSITION_GROUPS.map((group) => (
            <div key={group.label} className="space-y-1.5">
              <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider block">
                {group.label}
              </span>
              <div className="flex flex-wrap gap-2">
                {group.positions.map((pos) => {
                  const isSelectedPos = currentPosition === pos;
                  return (
                    <button
                      key={pos}
                      type="button"
                      disabled={isMatchCompleted}
                      onClick={() => handleJoinOrMove(selectedTeamKey, pos)}
                      className={`min-h-[40px] px-3.5 py-2 rounded-xl text-xs font-mono font-black transition flex items-center gap-1.5 touch-manipulation ${
                        isMatchCompleted
                          ? isSelectedPos
                            ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 opacity-100'
                            : 'bg-slate-900/50 text-slate-500 border border-slate-800/50 opacity-60'
                          : isSelectedPos
                          ? 'bg-emerald-500 text-slate-950 shadow-lg shadow-emerald-500/30 ring-2 ring-emerald-400 scale-105 active:scale-95'
                          : 'bg-slate-900 hover:bg-slate-800 text-slate-200 border border-slate-800 hover:border-slate-700 active:scale-95'
                      }`}
                    >
                      {pos}
                      {isSelectedPos && <Check className="w-3.5 h-3.5 stroke-[3]" />}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Save Toast Feedback */}
      {justSaved && (
        <div className="bg-emerald-500 text-slate-950 font-black text-xs p-3 rounded-2xl text-center shadow-lg animate-bounce flex items-center justify-center gap-2">
          <UserCheck className="w-4 h-4 stroke-[3]" />
          Joined {selectedTeamKey === 'teamA' ? teamA.name : teamB.name} as {selectedPosition}! Tactical pitch updated!
        </div>
      )}
    </div>
  );
};
